import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import {
  BpkCardV2,
  CARD_V2_VARIANTS,
} from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkDivider from '@skyscanner-internal/backpack-web/bpk-component-divider';
import {
  BpkBox,
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { useSelector } from 'react-redux';

import getFeatureDecision from '../../../../config';
import {
  ENABLE_CHEAPEST_DATES,
  ENABLE_COMBO_RECOMMENDATION,
  DATE_SELECTION_VERSION,
} from '../../../../config/constants';
import { MONTH_VIEW_RHR_CTA_CONTAINER } from '../../../../constants';
import { TOTAL_TRIP_COST_POSITION_CHANGED_BREAKPOINT } from '../../helpers/constants/displayConstants';
import { useCheapestCombinationsDispatch } from '../../helpers/hooks/useCheapestCombinationsDispatch';
import { useMediaQuery } from '../../helpers/hooks/useMediaQuery';
import { usePageLoadDiagnostic } from '../../helpers/hooks/usePageLoadDiagnostic';
import { useUpdateStrevdaDates } from '../../helpers/hooks/useUpdateStrevdaDates';
import { trackChokePoint } from '../../middleware/trackEvent';
import { useFlightPricing } from '../../network/hooks/flight-pricing/useFlightPricing';
import { useHotelPricing } from '../../network/hooks/hotel-pricing/useHotelPricing';
import { useRadarCreateSession } from '../../network/hooks/itineraries/useRadarCreateSession';
import { useRecommendations } from '../../network/hooks/michelin/useRecommendations';
import {
  isReturn as getIsReturn,
  isRequestFailureState,
} from '../../selectors';
import { shouldFireAutoselectChokePoint } from '../../selectors/cheapestDatesSelectors';
import AppErrorBoundary from '../AppErrorBoundary';
import CalendarChartSwitch from '../CalendarChartSwitch/CalendarChartSwitch';
import { DurationAutoselect } from '../DurationAutoSelect/DurationAutoselect';
import ErrorPanel from '../ErrorPanel/ErrorPanel';
import MonthViewCTAsTotalCost from '../MonthViewCTAsTotalCost/MonthViewCTAsTotalCost';
import MonthViewCalendarPanel from '../MonthViewCalendarPanel/MonthViewCalendarPanel';
import MonthViewChartPanel from '../MonthViewChartPanel/MonthViewChartPanel';
import MonthViewPageHeader from '../MonthViewPageHeader/MonthViewPageHeader';
import MonthViewPriceMessage from '../MonthViewPriceMessage/MonthViewPriceMessage';
import NoDirectsMessage from '../NoDirectsMessage/NoDirectsMessage';
import RecommendedForYou from '../RecommendedForYou/RecommendedForYou';

import type { Callbacks } from '../../../../services/contextAdaptor/types';

import './MonthView.scss';

export type MonthViewProps = {
  callbacks: Callbacks;
};

const MonthView = ({ callbacks }: MonthViewProps) => {
  const isReturn = useSelector(getIsReturn);
  const requestFailed = useSelector(isRequestFailureState);
  const enableCheapestDates = getFeatureDecision(ENABLE_CHEAPEST_DATES, false);
  const enableComboRecommendation = getFeatureDecision(
    ENABLE_COMBO_RECOMMENDATION,
    false,
  );
  const dateSelectionVersion = getFeatureDecision(
    DATE_SELECTION_VERSION,
    false,
  );
  const isMobilePhone = useMediaQuery(BREAKPOINTS.MOBILE);
  const showRhr = useMediaQuery(TOTAL_TRIP_COST_POSITION_CHANGED_BREAKPOINT);
  const cardDataAvailable = useSelector(shouldFireAutoselectChokePoint);
  const [rhrCtaContainer, setRhrCtaContainer] = useState<HTMLElement | null>(
    null,
  );

  // Dispatch cheapest combinations to Redux state
  useCheapestCombinationsDispatch();

  usePageLoadDiagnostic();

  useUpdateStrevdaDates(!!dateSelectionVersion);

  // Data fetching hooks
  useFlightPricing();
  useHotelPricing();
  useRadarCreateSession();
  // Hotel content enrichment (useHotelContent) is triggered in RecommendedForYou
  // once this recommendation resolves and a hotelId is available in Redux state.
  useRecommendations({ enabled: !!enableComboRecommendation });

  const shouldShowDurationAutoselect =
    enableCheapestDates && isReturn && cardDataAvailable;

  useEffect(() => {
    if (cardDataAvailable) {
      trackChokePoint('users_with_autoselect_return_trips_in_month_view');
    }
  }, [cardDataAvailable]);

  useEffect(() => {
    if (!requestFailed && !isReturn) {
      trackChokePoint('users_in_month_view_one_way');
    }
  }, [isReturn, requestFailed]);

  useLayoutEffect(() => {
    if (!showRhr) {
      setRhrCtaContainer(null);
      return;
    }

    setRhrCtaContainer(document.getElementById(MONTH_VIEW_RHR_CTA_CONTAINER));
  }, [showRhr]);

  const rhrCtas = <MonthViewCTAsTotalCost isReturn={isReturn} />;

  if (requestFailed) {
    return (
      <BpkBox
        as="main"
        id="main-content"
        position="relative"
        maxWidth="100%"
        tabIndex={-1}
      >
        <MonthViewPriceMessage />

        <AppErrorBoundary location="error-panel">
          <ErrorPanel
            helpUrl={callbacks.getHelpUrl()}
            reloadPage={callbacks.reloadPage}
          />
        </AppErrorBoundary>
      </BpkBox>
    );
  }

  return (
    <>
      <BpkBox
        as="main"
        id="main-content"
        position="relative"
        maxWidth="100%"
        tabIndex={-1}
      >
        <MonthViewPageHeader isMobilePhone={isMobilePhone} />
        <NoDirectsMessage />
        <BpkCardV2.Root variant={CARD_V2_VARIANTS.noElevation}>
          <BpkCardV2.Body padding={BpkSpacing.None}>
            <BpkStack
              direction="column"
              gap={BpkSpacing.None}
              paddingTop={{ base: BpkSpacing.None, tablet: BpkSpacing.LG }}
            >
              {shouldShowDurationAutoselect && <DurationAutoselect />}
              <CalendarChartSwitch>
                <MonthViewCalendarPanel isReturn={isReturn} />
                <MonthViewChartPanel isReturn={isReturn} />
              </CalendarChartSwitch>

              <BpkBox padding={BpkSpacing.Base}>
                <MonthViewPriceMessage />
                {isMobilePhone && <BpkDivider />}
              </BpkBox>

              {!showRhr && (
                <BpkBox
                  data-testid="month-view-inline-ctas"
                  paddingBlock={BpkSpacing.Base}
                  paddingInline={BpkSpacing.LG}
                >
                  <MonthViewCTAsTotalCost isReturn={isReturn} />
                </BpkBox>
              )}
            </BpkStack>
          </BpkCardV2.Body>
        </BpkCardV2.Root>

        {enableComboRecommendation && (
          <AppErrorBoundary location="recommendations" prefix="capybara_">
            <RecommendedForYou getResultUrl={callbacks.getResultUrl} />
          </AppErrorBoundary>
        )}
      </BpkBox>

      {showRhr &&
        (rhrCtaContainer ? (
          createPortal(rhrCtas, rhrCtaContainer)
        ) : (
          <BpkBox
            data-testid="month-view-rhr-cta-fallback"
            marginBlockEnd={BpkSpacing.Base}
          >
            <BpkCardV2.Root variant={CARD_V2_VARIANTS.noElevation}>
              <BpkCardV2.Body padding={BpkSpacing.Base}>
                {rhrCtas}
              </BpkCardV2.Body>
            </BpkCardV2.Root>
          </BpkBox>
        ))}
    </>
  );
};

export default MonthView;
